/**
 * Mobile arc Part B — Google OAuth for the Capacitor shell.
 *
 * ── B207/R14 (2026-09-26) — THE ASWebAuthenticationSession PLUGIN ─────────
 * Google blocks OAuth inside embedded WebViews, so the shell has always
 * needed to leave for an out-of-process browser and return on some kind of
 * callback. Until Build 33 that was the SYSTEM browser (`@capacitor/browser`,
 * SFSafariViewController) returning on a general-purpose deep link — a
 * mechanism with a real gap: `tayro://` is a private-use scheme with no OS
 * ownership check, so another app claiming the same scheme could, in
 * principle, be handed a navigation meant for Tayro (spec-signin-auth-session.md
 * §1 has the full writeup). Build 33 instead opens an in-house
 * `ASWebAuthenticationSession` (`local-plugins/tayro-native-auth`,
 * `lib/nativeAuthSession.ts`) whose HTTPS callback
 * (`https://tayro.run/auth/mobile-callback`) is delivered directly back to
 * the SAME in-process session object that started it, proven by an
 * associated WEB CREDENTIALS domain (`webcredentials:tayro.run`,
 * `App.entitlements` + the AASA's `webcredentials` key) Apple checks against
 * THIS app's own Team ID + bundle ID.
 *
 * Builds 30-32 are already out in the wild returning on the custom scheme
 * deep link `tayro://auth-callback` (registered in Info.plist) — but on their
 * OWN already-compiled JS bundle, frozen at build time. THIS source file only
 * ever ships inside Build 33 and later, so nothing in it — including
 * `registerNativeAuthListener`'s match, below — has ever been what keeps
 * those installed apps working; removing the `tayro://` match here (security
 * review F2) changes nothing for them. `NATIVE_AUTH_CALLBACK` is kept only as
 * a stable name for that now-retired scheme (Step B, spec §1.3, removes
 * `tayro://` from both Supabase projects' redirect allowlists only once the
 * family has Build 33 — a founder dashboard action, not anything this module
 * does).
 *
 * Flow (Supabase PKCE — B207 direction B, still in effect: the callback
 * carries a one-time code instead of live tokens, so another app that also
 * received it gets a code it cannot redeem):
 *   1. signInWithOAuth(skipBrowserRedirect) → hosted auth URL (the SDK
 *      appends code_challenge/code_challenge_method itself, since the
 *      shared client is built with flowType: 'pkce' — see lib/supabase.ts)
 *   2. plugin.authenticate(url, host, path) → Google → Supabase callback →
 *      the session's own https callback (Builds 30-32's frozen bundles still
 *      run their own separate Browser.open + tayro:// deep link, but that is
 *      code this file no longer affects — see the note above)
 *   3. the plugin's promise resolves with the callback URL directly — no
 *      separate `appUrlOpen` event for Build 33's own flow at all, unlike
 *      the old Browser.open design. `openNativeOAuth` is now the WHOLE round
 *      trip: it parses the code and exchanges it itself (see
 *      `completeAuthCallback` / `commitAuthCode` below), instead of returning
 *      early and leaving the exchange to a separate listener.
 *   4. exchangeCodeForSession(code) → onAuthStateChange updates the app like
 *      any sign-in. (The web redirect is unaffected: the SDK's own
 *      detectSessionInUrl exchanges a web `?code=` automatically, no code
 *      change there.)
 *
 * `registerNativeAuthListener`'s `appUrlOpen` listener stays — Builds 30-32
 * complete their own frozen `Browser.open` flow on their OWN bundled copy of
 * this listener, not this one (see the note above), so this file's copy is
 * defense-in-depth for Build 33 ONLY: a callback arriving as a plain deep
 * link instead of being intercepted by the session (spec §4.6). The static
 * fallback page at `https://tayro.run/auth/mobile-callback`
 * (`public/auth-mobile-callback.html`) never exchanges the code itself, so a
 * tap that reaches this listener still completes normally on the https
 * shape; the retired `tayro://` shape is no longer matched at all (security
 * review F2 — see `NATIVE_AUTH_CALLBACK`'s own doc below).
 *
 * All native plugin imports are dynamic: this module is statically imported
 * by AuthContext, and the web bundle must not grow native plugin code.
 *
 * ── FIELD-TEST F1 (Build 6) — universal links share this listener ─────────
 * Once the Associated Domains entitlement shipped (founder-only, Build 7),
 * iOS delivers a tapped `https://tayro.run/join/<code>` through the SAME
 * `appUrlOpen` event. There is exactly one listener for that event and it
 * lives here, so the join link is handled as the else-branch of the auth
 * check rather than by a second listener racing this one. All it does is
 * stash — see `lib/team/joinDeepLink.ts` for why a link may never join.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { handleJoinDeepLink } from './team/joinDeepLink';
import { getNativeAuthSessionPlugin, type NativeAuthSessionPlugin } from './nativeAuthSession';

export const NATIVE_AUTH_CALLBACK = 'tayro://auth-callback';

/**
 * B207/R14 (Build 33) — the https callback `openNativeOAuth` requests as its
 * `redirectTo`, and the `host`/`path` pair the plugin's `authenticate` call
 * needs for `ASWebAuthenticationSessionCallback.https`. Renamed from the held
 * lane's `NATIVE_AUTH_CALLBACK_UNIVERSAL_LINK` (criterion 3,
 * spec-signin-auth-session.md): the mechanism is no longer a universal link —
 * the AASA's `applinks` claim for this path was dropped in favor of the
 * `webcredentials` key `ASWebAuthenticationSessionCallback.https` actually
 * needs (see `App.entitlements` and the AASA file). `NATIVE_AUTH_CALLBACK`
 * above is kept only as the historical name for Builds 30-32's own frozen
 * `tayro://` scheme; `registerNativeAuthListener` no longer matches it
 * (security review F2 — see that function's own doc) since this source file
 * never runs inside an already-shipped Build 30-32 binary anyway (see the
 * module doc).
 */
export const NATIVE_AUTH_CALLBACK_HTTPS = 'https://tayro.run/auth/mobile-callback';
export const NATIVE_AUTH_CALLBACK_HOST = 'tayro.run';
export const NATIVE_AUTH_CALLBACK_PATH = '/auth/mobile-callback';

/**
 * F1 — `App.getLaunchUrl()` is read at most ONCE per process. See the call
 * site for why (it is not a launch URL; it is the last URL the process saw,
 * and it replays).
 */
let launchUrlRead = false;

/** Test-only: the module-scope once-per-process latch has to be resettable
 *  between cases. Mirrors `__resetNativePushForTest` in lib/nativePush.ts. */
export function __resetJoinLaunchUrlForTest(): void {
  launchUrlRead = false;
}

/**
 * b178-fix4 — the wedged-bridge escape for the Google door, mirrored from
 * `lib/appleAuth.ts`'s `APPLE_SHEET_TIMEOUT_MS` / `withAppleTimeout`: same
 * class of failure (the rakes ledger's Capacitor-bridge-wedge class — a
 * plugin call the native side drops instead of settling never resolves OR
 * rejects), same order of magnitude.
 *
 * B207/R14 — this ONE bound now covers TWO legs, exactly like
 * `AppleSheetTimeoutError` covers both Apple's `authorize()` and its
 * `exchangeIdentityToken()`: (1) `plugin.authenticate(...)` never settling
 * (nothing ever appeared, or the athlete is still working through the
 * sheet — see the late-completion continuation below), and (2) the PKCE
 * code exchange itself stalling as a plain network call.
 */
export const NATIVE_OAUTH_TIMEOUT_MS = 30_000;

/**
 * The value `withNativeOAuthTimeout` actually uses. Production never changes
 * it. Exists so a real-timer test can exercise the real `openNativeOAuth`
 * entry point on a short budget — see `appleAuth.ts`'s identical
 * `appleTimeoutMs` for why fake-timer tests of an internal helper are not
 * enough to prove this guarantee.
 */
let nativeOAuthTimeoutMs: number = NATIVE_OAUTH_TIMEOUT_MS;

/** Test-only: shorten the budget so a real-timer test finishes in ms.
 *  Call with no argument to restore the shipped value. */
export function __setNativeOAuthTimeoutForTest(ms?: number): void {
  nativeOAuthTimeoutMs = ms ?? NATIVE_OAUTH_TIMEOUT_MS;
}

/**
 * B231-style (ported from `appleAuth.ts`'s `APPLE_LATE_COMPLETION_BOUND_MS`)
 * — the absolute ceiling on the late-completion continuation below. Without
 * it, a `plugin.authenticate()` call that never settles at all could still
 * sign the athlete in minutes, or hours, later. Generous on purpose — the
 * continuation exists to rescue a sheet that was merely SLOW (2FA, a typed
 * password), not wedged; the 30s wait above already covers "nothing
 * appeared".
 */
export const NATIVE_OAUTH_LATE_COMPLETION_BOUND_MS = 5 * 60_000;

let nativeOAuthLateCompletionBoundMs: number = NATIVE_OAUTH_LATE_COMPLETION_BOUND_MS;

/** Test-only: shorten the late-completion bound. No argument restores it. */
export function __setNativeOAuthLateCompletionBoundForTest(ms?: number): void {
  nativeOAuthLateCompletionBoundMs = ms ?? NATIVE_OAUTH_LATE_COMPLETION_BOUND_MS;
}

/**
 * Typed timeout — matched by `instanceof` in `mapOAuthError` so a wedged
 * bridge or a stalled exchange reads as "not ready", not as a network
 * failure the keyword heuristics would otherwise misfile it as (the message
 * says "timed out").
 */
export class NativeBridgeTimeoutError extends Error {
  constructor() {
    super('Native sign-in timed out before it could complete.');
    this.name = 'NativeBridgeTimeoutError';
  }
}

/**
 * B207/R14 — the native plugin is not in this binary. Raised by
 * `openNativeOAuth` itself (unlike Apple's `ApplePluginUnavailableError`,
 * which the CALLER raises from an outcome enum) because this flow has no such
 * enum: a missing plugin is a hard failure here, never a silent reroute to
 * the old `Browser.open` path — exactly the lesson `appleAuth.ts`'s own
 * history states in plain words: "a silent fallback masks a broken native
 * binary on exactly the surface App Review judges."
 */
export class NativeOAuthPluginUnavailableError extends Error {
  constructor() {
    super('Native sign-in plugin is not available in this build.');
    this.name = 'NativeOAuthPluginUnavailableError';
  }
}

/** The plugin rejects a user-cancelled sheet with a message containing the
 *  literal substring "cancelled" (`NativeAuthPlugin.swift`). Matched the same
 *  lightweight way `appleAuth.ts`'s `isAppleCancellation` falls back to a
 *  message check: no error code is reliably available across the bridge. */
function isNativeAuthCancellation(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '');
  return /cancel/i.test(message);
}

/** Race `work` against the wedged-bridge timeout. `work` is not cancelled by
 *  the race itself (a native call cannot be) — `Promise.race` stays
 *  subscribed to it, so a late settle is never an unhandled rejection.
 *  `onTimeout`, when given, runs synchronously the instant the timer fires,
 *  before the race rejects — the caller's chance to actually tear down
 *  `work` (a real `AbortController.abort()`), not merely stop awaiting it.
 *  Same shape as `appleAuth.ts`'s `withAppleTimeout`. */
function withNativeOAuthTimeout<T>(work: Promise<T>, onTimeout?: () => void): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      onTimeout?.();
      reject(new NativeBridgeTimeoutError());
    }, nativeOAuthTimeoutMs);
  });
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

// ── the callback URL — parsed once, shared by both consumers ────────────────

type AuthCallbackParseResult =
  | { code: string }
  | { errorDescription: string }
  | { missingCode: true };

/** Pure extraction of the query/fragment-parsing and code/error-extraction
 *  logic both `openNativeOAuth` and `registerNativeAuthListener` need. Not a
 *  rewrite of the parsing itself — the fragment-first-then-query check and
 *  `URLSearchParams` lookup are unchanged from before this lane. */
function parseAuthCallbackUrl(url: string): AuthCallbackParseResult {
  const fragment = url.includes('#') ? url.slice(url.indexOf('#') + 1) : '';
  const query = url.includes('?') ? url.slice(url.indexOf('?') + 1).split('#')[0] : '';
  const params = new URLSearchParams(fragment || query);
  const errorDescription = params.get('error_description') || params.get('error');
  if (errorDescription) return { errorDescription };
  const code = params.get('code');
  if (!code) return { missingCode: true };
  return { code };
}

/**
 * The straight extraction of what `registerNativeAuthListener` has always
 * done on a matching callback: parse, exchange, report. Used UNCHANGED by
 * that listener, now for Build 33's own defense-in-depth arrival on the
 * static fallback page only (spec §4.6) — the retired `tayro://` shape is no
 * longer matched at all (security review F2 — see `NATIVE_AUTH_CALLBACK`'s
 * own doc).
 *
 * `openNativeOAuth`'s OWN plugin-driven exchange does NOT call this function —
 * see `commitAuthCode` below for why (Open Question 3,
 * spec-signin-auth-session.md: `exchangeCodeForSession` shares
 * `signInWithIdToken`'s "commits internally, cannot be raced honestly" shape,
 * confirmed against the vendored `@supabase/auth-js` source in
 * `nativeAuth.pkceExchange.test.ts`, so the plugin-driven path — which DOES
 * need to race this call against a real, abortable timeout — uses its own
 * direct exchange instead). This is a deliberate, stated deviation from a
 * literal "one function, every caller" reading: the two paths share the pure
 * PARSING logic (`parseAuthCallbackUrl`) but not the exchange call itself,
 * because only the plugin-driven path can ever have a newer sign-in race it.
 */
async function completeAuthCallback(
  client: SupabaseClient,
  url: string,
  onError: (message: string) => void,
): Promise<void> {
  const parsed = parseAuthCallbackUrl(url);
  if ('errorDescription' in parsed) {
    onError(parsed.errorDescription);
    return;
  }
  if ('missingCode' in parsed) {
    onError('Sign-in callback was missing a code.');
    return;
  }
  const { error } = await client.auth.exchangeCodeForSession(parsed.code);
  if (error) onError(error.message);
}

// ── the guarded direct exchange — ported from appleAuth.ts's B231 pattern ──

/**
 * ⚖️ B231-style (ported from `appleAuth.ts`'s `ExchangeGuard`) — the guard a
 * caller threads through `commitAuthCode` so it can decline to sign in rather
 * than land invisibly late. Same four fields, same meaning, adapted from an
 * identity-token exchange to a PKCE code exchange:
 *
 *   · `baseline` — the access token as it stood right BEFORE this exchange
 *     attempt began, for `hasNewerSession` below. `undefined` means "capture
 *     it now" (the ordinary path); the late-completion continuation passes
 *     its own snapshot, taken at the ORIGINAL timeout instant, through
 *     unchanged.
 *   · `deadline` — a REAL abort, not just a flag: `controller.abort()` is
 *     called by the caller's own timer the instant it gives up on this call,
 *     genuinely tearing down the in-flight exchange fetch (see
 *     `exchangeAuthCodeDirect`) rather than merely ignoring its eventual
 *     answer.
 *   · `supersededByAuthEvent` — a LIVE drop, not just a one-time snapshot:
 *     only the late-completion continuation wires this (it can run for
 *     minutes); the ordinary path's own short window is bracketed tightly
 *     enough by the baseline-at-entry and baseline-right-before-commit
 *     checks alone.
 *   · `ownAccessToken` — filled in by `commitAuthCode` the instant this
 *     attempt's own token is known, so the late continuation's
 *     `SIGNED_IN`/`SIGNED_OUT` listener (armed long before that token exists)
 *     can tell its own eventual commit apart from a different account's.
 */
interface ExchangeGuard {
  baseline?: Promise<string | null> | string | null;
  deadline?: { expired: boolean; controller: AbortController };
  supersededByAuthEvent?: { current: boolean };
  ownAccessToken?: { current: string | null };
}

/** The current session's access token, or null. Never throws — a read that
 *  fails must not decide anything on its own. Local copy of `appleAuth.ts`'s
 *  identical helper: that module is outside this lane's ownership, and the
 *  house pattern here is a self-contained file per provider door (Apple's own
 *  timeout/guard machinery is duplicated, not shared, for the same reason). */
async function currentAccessToken(client: SupabaseClient): Promise<string | null> {
  try {
    const { data } = await client.auth.getSession();
    return data?.session?.access_token ?? null;
  } catch {
    return null;
  }
}

/** "Did a DIFFERENT sign-in land while this call was wedged?" Identity
 *  comparison, not a clock comparison — see `appleAuth.ts`'s identical
 *  helper for the full reasoning (a local copy for the same reason as
 *  `currentAccessToken` above). Fails CLOSED. */
async function hasNewerSession(client: SupabaseClient, before: string | null): Promise<boolean> {
  try {
    const { data } = await client.auth.getSession();
    const now = data?.session?.access_token ?? null;
    if (now === null) return false; // still signed out — the rescue is safe.
    return now !== before;
  } catch {
    return true;
  }
}

/** The self sign-out cannot fail open. One retry of the public `signOut`,
 *  then the same local-only removal fallback `appleAuth.ts` uses (a local
 *  copy — see `currentAccessToken` above for why). */
async function forceLocalSignOut(client: SupabaseClient): Promise<void> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { error } = await client.auth.signOut({ scope: 'local' });
      if (!error) return;
    } catch {
      // A thrown network failure ends signed-in exactly like a returned
      // `{ error }` — fall through to the retry, then the local removal.
    }
  }
  const auth = client.auth as unknown as { _removeSession?: () => Promise<void> };
  if (typeof auth._removeSession === 'function') {
    try {
      await auth._removeSession();
    } catch {
      /* nothing left to try — best effort past this point */
    }
  } else {
    // eslint-disable-next-line no-console
    console.warn(
      '[auth] forceLocalSignOut: signOut failed twice and _removeSession is not a function on this auth client — the local session may still be stored.',
    );
  }
}

/**
 * The PKCE code_verifier `signInWithOAuth` stashed before the browser/plugin
 * hop, read the same way the vendored `_exchangeCodeForSession` reads it
 * (`nativeAuth.pkceExchange.test.ts` pins this shape against the real
 * `@supabase/auth-js` source): `${storageKey}-code-verifier`, a
 * `codeVerifier/redirectType` string, JSON-parse attempted first with a
 * raw-string fallback (a code verifier is never valid JSON in practice, so
 * this always falls through — matched for fidelity anyway).
 */
async function getStoredCodeVerifier(
  client: SupabaseClient,
): Promise<{ codeVerifier: string; redirectType: string | null } | null> {
  const auth = client.auth as unknown as {
    storage: { getItem: (key: string) => unknown };
    storageKey: string;
  };
  const raw = await Promise.resolve(auth.storage.getItem(`${auth.storageKey}-code-verifier`));
  if (!raw || typeof raw !== 'string') return null;
  let value: unknown = raw;
  try {
    value = JSON.parse(raw);
  } catch {
    /* not JSON — the expected shape for a code verifier */
  }
  const [codeVerifier, redirectType] = String(value ?? '').split('/');
  if (!codeVerifier) return null;
  return { codeVerifier, redirectType: redirectType ?? null };
}

/** Best-effort — mirrors the SDK's own `removeItemAsync` cleanup, which the
 *  real `_exchangeCodeForSession` runs after every attempt, success or
 *  failure. Never throws: a failed cleanup must not turn into a sign-in
 *  failure. */
async function clearStoredCodeVerifier(client: SupabaseClient): Promise<void> {
  const auth = client.auth as unknown as {
    storage: { removeItem: (key: string) => unknown };
    storageKey: string;
  };
  try {
    await Promise.resolve(auth.storage.removeItem(`${auth.storageKey}-code-verifier`));
  } catch {
    /* best effort */
  }
}

/**
 * ⚖️ B231-style (ported from `appleAuth.ts`'s `exchangeIdTokenDirect`) — THE
 * DIRECT EXCHANGE.
 *
 * `client.auth.exchangeCodeForSession` cannot be raced honestly: the
 * installed `@supabase/auth-js`'s `_exchangeCodeForSession` calls
 * `_saveSession` and fires `SIGNED_IN` from INSIDE its own promise the
 * instant its own fetch resolves — however late — with no `AbortSignal` on
 * its public shape. `nativeAuth.pkceExchange.test.ts` pins this same shape
 * against the vendored source, mirroring `appleAuth.directExchangeShape.test.ts`
 * for `signInWithIdToken` (Open Question 3, spec-signin-auth-session.md: the
 * shapes match, so the same direct-exchange-under-a-real-AbortSignal pattern
 * applies here too).
 *
 * So this function makes the SAME request `_exchangeCodeForSession` makes —
 * `POST {url}/token?grant_type=pkce`, `{ auth_code, code_verifier }` — under
 * a REAL `AbortSignal`, and returns the raw tokens rather than committing
 * them. `commitAuthCode` decides whether to commit.
 *
 * ⚠️ `client.auth.url` / `.headers` are `protected` in `@supabase/auth-js`'s
 * own `.d.ts` — reached into here only because there is no supported public
 * API for a cancellable code exchange, the same reach `appleAuth.ts` already
 * makes for the identical reason.
 */
async function exchangeAuthCodeDirect(
  client: SupabaseClient,
  params: { code: string; codeVerifier: string; signal: AbortSignal },
): Promise<{ access_token: string; refresh_token: string; user_id: string | null }> {
  const auth = client.auth as unknown as { url?: unknown; headers?: unknown };
  if (typeof auth.url !== 'string' || !auth.url || typeof auth.headers !== 'object' || !auth.headers) {
    throw new Error(
      'Native sign-in: the Supabase auth client did not expose the url/headers shape the direct exchange needs.',
    );
  }
  const res = await fetch(`${auth.url}/token?grant_type=pkce`, {
    method: 'POST',
    headers: { ...(auth.headers as Record<string, string>), 'Content-Type': 'application/json;charset=UTF-8' },
    body: JSON.stringify({
      auth_code: params.code,
      code_verifier: params.codeVerifier,
    }),
    signal: params.signal,
  });
  const body: unknown = await res.json().catch(() => ({}));
  const b = (body ?? {}) as Record<string, unknown>;
  if (!res.ok) {
    const message =
      (typeof b.msg === 'string' && b.msg) ||
      (typeof b.error_description === 'string' && b.error_description) ||
      (typeof b.error === 'string' && b.error) ||
      `Native sign-in exchange failed (${res.status})`;
    throw new Error(message);
  }
  const accessToken = b.access_token;
  const refreshToken = b.refresh_token;
  if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') {
    throw new Error('Native sign-in exchange returned no session.');
  }
  // The raw GoTrue token response carries `user` at the top level, same shape
  // `appleAuth.ts`'s own direct exchange already depends on.
  const userObj = b.user;
  const userId =
    userObj && typeof userObj === 'object' && typeof (userObj as Record<string, unknown>).id === 'string'
      ? ((userObj as Record<string, unknown>).id as string)
      : null;
  return { access_token: accessToken, refresh_token: refreshToken, user_id: userId };
}

/**
 * ⚖️ B231-style (ported from `appleAuth.ts`'s `exchangeIdentityToken`) —
 * decide whether THIS attempt's PKCE code exchange is the one left standing.
 * Mirrors that function's shape field-for-field (see `ExchangeGuard`);
 * resolves `true` only when this attempt is the one that actually committed.
 */
async function commitAuthCode(
  client: SupabaseClient,
  code: string,
  guard?: ExchangeGuard,
): Promise<boolean> {
  const baseline = guard?.baseline !== undefined ? await guard.baseline : await currentAccessToken(client);
  // Security 4 parity with appleAuth.ts — wait for the client's OWN init
  // first; see that module's identical comment for the full boot-race
  // reasoning this also guards against.
  await client.auth.initialize();
  if (guard?.deadline?.expired) return false;
  if (guard?.supersededByAuthEvent?.current) return false;
  if (await hasNewerSession(client, baseline)) return false;

  const controller = guard?.deadline?.controller ?? new AbortController();
  let tokens: { access_token: string; refresh_token: string; user_id: string | null };
  try {
    const stored = await getStoredCodeVerifier(client);
    if (!stored) {
      // B207's own point: an app other than Tayro that somehow received this
      // callback has no matching verifier and gets exactly this failure,
      // never a session.
      throw new Error('No matching code verifier for this sign-in attempt.');
    }
    tokens = await exchangeAuthCodeDirect(client, {
      code,
      codeVerifier: stored.codeVerifier,
      signal: controller.signal,
    });
  } catch (err) {
    if (controller.signal.aborted) return false;
    throw err;
  } finally {
    await clearStoredCodeVerifier(client);
  }
  if (guard?.ownAccessToken) guard.ownAccessToken.current = tokens.access_token;

  if (guard?.deadline?.expired) return false;
  if (guard?.supersededByAuthEvent?.current) return false;
  if (await hasNewerSession(client, baseline)) return false;

  const ownRace = guard?.supersededByAuthEvent ? null : { current: false };
  const raceSub = ownRace
    ? client.auth.onAuthStateChange((event, session) => {
        if (event !== 'SIGNED_IN' && event !== 'SIGNED_OUT') return;
        if (session?.access_token === tokens.access_token) return; // our own commit
        ownRace.current = true;
      })
    : null;

  let setSessionError: Awaited<ReturnType<typeof client.auth.setSession>>['error'] = null;
  try {
    ({ error: setSessionError } = await client.auth.setSession({
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
    }));
  } finally {
    raceSub?.data.subscription.unsubscribe();
  }
  if (setSessionError) throw setSessionError;

  if (guard?.deadline?.expired || guard?.supersededByAuthEvent?.current || ownRace?.current) {
    let sessionResult: Awaited<ReturnType<typeof client.auth.getSession>> | null = null;
    try {
      sessionResult = await client.auth.getSession();
    } catch {
      /* treated as "could still be ours" below */
    }
    if (!sessionResult || sessionResult.error) {
      await forceLocalSignOut(client);
      return false;
    }
    const { data } = sessionResult;
    const stillOurs = tokens.user_id
      ? data?.session?.user?.id === tokens.user_id
      : data?.session?.access_token === tokens.access_token;
    if (stillOurs) {
      await forceLocalSignOut(client);
    }
    return false;
  }

  return true;
}

// ── the entry point ──────────────────────────────────────────────────────

/**
 * Kick off sign-in via the in-house `ASWebAuthenticationSession` plugin, and
 * complete the WHOLE round trip — Build 33's own flow no longer returns
 * early for a separate deep-link listener to finish (see the module doc).
 * Throws on failure — the caller owns error→chip mapping (`mapOAuthError`),
 * except a cancelled sheet, which resolves quietly (no chip), matching
 * `signInWithAppleNative`'s `'cancelled'` contract.
 *
 * apple-login-v1: `provider` was added with a 'google' DEFAULT so the
 * existing call site is unchanged. Apple normally uses the NATIVE sheet
 * (lib/appleAuth.ts); per spec-signin-auth-session.md §4.3, its degraded
 * fallback through this function is not currently wired up by any live call
 * site — `AuthContext.tsx`'s Apple outcome handling surfaces
 * `ApplePluginUnavailableError` directly instead. This function stays
 * provider-generic anyway so a future re-wiring inherits the same
 * session-based design for free.
 */
export async function openNativeOAuth(
  client: SupabaseClient,
  provider: 'google' | 'apple' = 'google',
): Promise<void> {
  const plugin = await getNativeAuthSessionPlugin();
  if (!plugin) throw new NativeOAuthPluginUnavailableError();

  const { data, error } = await client.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: NATIVE_AUTH_CALLBACK_HTTPS,
      ...(provider === 'google' ? { queryParams: { prompt: 'select_account' } } : {}),
      skipBrowserRedirect: true,
    },
  });
  if (error) throw error;
  if (!data?.url) throw new Error('No OAuth URL returned');

  const authenticatePromise = plugin.session.authenticate({
    url: data.url,
    host: NATIVE_AUTH_CALLBACK_HOST,
    path: NATIVE_AUTH_CALLBACK_PATH,
  });

  let result: { url: string };
  try {
    result = await withNativeOAuthTimeout(authenticatePromise);
  } catch (err) {
    if (err instanceof NativeBridgeTimeoutError) {
      armLateCompletion(client, plugin, authenticatePromise);
      throw err;
    }
    if (isNativeAuthCancellation(err)) return; // dismissing the sheet is not an error
    throw err;
  }

  const parsed = parseAuthCallbackUrl(result.url);
  if ('errorDescription' in parsed) throw new Error(parsed.errorDescription);
  if ('missingCode' in parsed) throw new Error('Sign-in callback was missing a code.');

  // The exchange itself is a plain network call, raced the same way Apple's
  // own `exchangeIdentityToken` is: a REAL abort on timeout, reusing
  // `NativeBridgeTimeoutError` for the same reason `AppleSheetTimeoutError`
  // covers both of that module's legs (see that error class's own doc).
  //
  // The resolved boolean (committed vs. lost-a-race) is intentionally not
  // inspected here: a genuine failure already THROWS past this await (a
  // missing code_verifier, a `setSession` error), and a timeout already
  // rejects via the race above — the only way this call RESOLVES `false` is
  // the same "this attempt safely signed itself back out" case Apple's own
  // `committed ? 'signed-in' : 'cancelled'` treats as quiet, no chip, nothing
  // wrong to report. Resolving `openNativeOAuth` either way is that same
  // quiet contract.
  const exchangeController = new AbortController();
  const exchangeDeadline = { expired: false, controller: exchangeController };
  await withNativeOAuthTimeout(
    commitAuthCode(client, parsed.code, { deadline: exchangeDeadline }),
    () => {
      exchangeDeadline.expired = true;
      exchangeController.abort();
    },
  );
}

/**
 * ⚖️ B231-style (ported from `appleAuth.ts`'s late-completion continuation in
 * `signInWithAppleNative`) — the 30s wait above only stops WAITING. If
 * `authenticatePromise` later resolves anyway (the athlete was genuinely
 * still working through 2FA or a slow password), finish the exchange in the
 * background so a legitimately slow sign-in still lands the athlete signed
 * in — UNLESS a newer sign-in has already landed, or this continuation's own
 * absolute bound expires first, in which case `plugin.cancel()` is called as
 * the REAL teardown (the B231 finding-2 lesson, ported from "abort the
 * fetch" to "dismiss the session" — merely abandoning the await would leave
 * a browser sheet on screen with nothing watching it, which Apple's own
 * plugin cannot do but this one can).
 */
function armLateCompletion(
  client: SupabaseClient,
  plugin: { session: NativeAuthSessionPlugin },
  authenticatePromise: Promise<{ url: string }>,
): void {
  const baseline = currentAccessToken(client);
  const lateController = new AbortController();
  const lateDeadline = { expired: false, controller: lateController };
  const supersededByAuthEvent = { current: false };
  const ownAccessToken: { current: string | null } = { current: null };

  const teardown = () => {
    void plugin.session.cancel().catch(() => {
      /* best effort — the completion handler settling is what actually matters */
    });
  };

  const { data: lateAuthSub } = client.auth.onAuthStateChange((event, session) => {
    if (event !== 'SIGNED_IN' && event !== 'SIGNED_OUT') return;
    if (ownAccessToken.current && session?.access_token === ownAccessToken.current) return;
    supersededByAuthEvent.current = true;
    lateController.abort();
    teardown();
  });
  const lateBoundTimer = setTimeout(() => {
    lateDeadline.expired = true;
    lateController.abort();
    teardown();
  }, nativeOAuthLateCompletionBoundMs);

  void authenticatePromise
    .then(async (res) => {
      const parsed = parseAuthCallbackUrl(res.url);
      if (!('code' in parsed)) return; // an error/missing-code late arrival: nothing to rescue
      await commitAuthCode(client, parsed.code, {
        baseline,
        deadline: lateDeadline,
        supersededByAuthEvent,
        ownAccessToken,
      });
    })
    .catch(() => {
      /* late failure — the chip for the timeout already said "not responding" */
    })
    .finally(() => {
      clearTimeout(lateBoundTimer);
      lateAuthSub.subscription.unsubscribe();
    });
}

/**
 * Listen for the auth deep link and complete the session. Returns a
 * cleanup function. `onError` receives a human-mappable message when
 * the callback carries an error (e.g. the athlete denied consent).
 *
 * Also the app's single `appUrlOpen` subscription: a URL that is not the
 * auth callback is offered to the join-link handler (F1) and otherwise
 * ignored.
 *
 * B207/R14/F2 (security review) — the match is `NATIVE_AUTH_CALLBACK_HTTPS`
 * only: Build 33's own defense-in-depth arrival — `openNativeOAuth` no longer
 * requests this as a plain deep link in the ordinary case, since the plugin
 * intercepts it directly, but a callback can still land here if that
 * interception fails (spec §4.6). The old `tayro://` scheme
 * (`NATIVE_AUTH_CALLBACK`) is NOT matched here: Builds 30-32 already shipped
 * with their own frozen copy of this listener that still accepts it, so this
 * file's match was never what kept them working, and Build 33 itself never
 * requests or needs that scheme.
 */
export async function registerNativeAuthListener(
  client: SupabaseClient,
  onError: (message: string) => void,
): Promise<() => void> {
  const { App } = await import('@capacitor/app');
  const handle = await App.addListener('appUrlOpen', ({ url }) => {
    if (!url.startsWith(NATIVE_AUTH_CALLBACK_HTTPS)) {
      // F1 — the only other deep link we answer to is
      // https://tayro.run/join/<code>. handleJoinDeepLink returns null for
      // everything else, so an unrecognised URL is still a no-op.
      handleJoinDeepLink(url);
      return;
    }
    void (async () => {
      try {
        await completeAuthCallback(client, url, onError);
      } finally {
        // Dismiss the in-app browser sheet either way. Build 33's own
        // plugin-driven flow never opens `Browser` at all, so this close is
        // a harmless no-op when a callback reaches this listener from that
        // build (Browser.close on nothing already open). Builds 30-32 run
        // their own separate, already-shipped copy of this same logic — not
        // this file — so nothing here affects them.
        try {
          const { Browser } = await import('@capacitor/browser');
          await Browser.close();
        } catch {
          // Already closed (user swiped it away) — fine.
        }
      }
    })();
  });

  // ── F1 · getLaunchUrl — belt-and-braces, once per process, never awaited ──
  //
  // What this is NOT: a cold-start necessity. Capacitor 8's AppPlugin retains
  // the cold-start URL (`retainUntilConsumed`) and replays it into
  // `appUrlOpen` once JS subscribes, so the listener above already covers the
  // launched-by-link case. This call is kept only because a missed invite is
  // a silent dead end and a duplicate stash costs nothing (same code, still
  // consumed exactly once by <JoinCodeMount>).
  //
  // Why the latch: `getLaunchUrl()` does not return a launch URL. It returns
  // `ApplicationDelegateProxy.lastURL` — the LAST url this process saw. Read
  // it again after a WebView reload or a StrictMode remount and it replays an
  // already-spent invite, re-opening the join confirmation for a team the
  // athlete has already joined. Tap-gated, so never an unauthorised write,
  // but confusing. `launchUrlRead` is claimed SYNCHRONOUSLY so two overlapping
  // registrations cannot both slip through.
  //
  // Why fire-and-forget: AuthContext awaits this function before getSession,
  // so this sits on the sign-in critical path. try/catch covers a rejection
  // but not a bridge promise that never settles — awaiting it would let a
  // wedged plugin hang login. Nothing downstream needs the result.
  if (!launchUrlRead) {
    launchUrlRead = true;
    try {
      void App.getLaunchUrl()
        .then((launch) => {
          if (launch?.url) handleJoinDeepLink(launch.url);
        })
        .catch(() => {
          /* bridge unavailable — appUrlOpen is the primary path anyway */
        });
    } catch {
      /* synchronous throw from the bridge — same degradation */
    }
  }

  return () => {
    void handle.remove();
  };
}
